import React, { useCallback, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { offersSubscriptionsQuery } from "./offerAnalytics.queries";
import { groupByOffer } from "./offerAnalytics.utils";
import {
  getSubscriptionHref,
  subscriptionColumns,
} from "./tableParams/subscriptionColumns";
import useAbility from "../../../hooks/useAbility";
import DataTable from "../../dataTable/DataTable";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";

import type { JSX } from "react";

import type { OfferAnalyticsFilterValues } from "./offerAnalytics.types";
import type { SubscriptionRow } from "./tableParams/subscriptionColumns.types";
import type { AppState } from "../../../reducers";

interface SubscriptionsTableProps {
  filters: OfferAnalyticsFilterValues;
}

function SubscriptionsTable({ filters }: SubscriptionsTableProps): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t } = useTranslation("editor-analytics");
  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );
  const { canAccess } = useAbility();
  const canWrite = canAccess(orgaOptions).project.salesWrite;

  const {
    data: offersSubscriptions,
    isLoading,
    isPreviousData,
    isError,
    refetch,
  } = useQuery(offersSubscriptionsQuery(projectSlug, filters));

  const rowList = useMemo(
    () => groupByOffer(offersSubscriptions?.data ?? []),
    [offersSubscriptions?.data],
  );

  const columnList = useMemo(
    () => subscriptionColumns({ t, projectSlug, canWrite }),
    [t, projectSlug, canWrite],
  );

  const getRowHref = useCallback(
    (row: SubscriptionRow): string | undefined =>
      getSubscriptionHref(row, projectSlug, canWrite),
    [canWrite, projectSlug],
  );

  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle>{t("viewOffersTitle")}</CardTitle>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columnList}
          data={rowList}
          getRowHref={getRowHref}
          isError={isError}
          isLoading={isLoading || isPreviousData}
          onRetry={refetch}
          urlSync={{ prefix: "subscriptions" }}
          resetPageOn={[
            filters.startDate.valueOf(),
            filters.endDate.valueOf(),
            filters.offerUuid,
          ]}
        />
      </CardContent>
    </Card>
  );
}

export default SubscriptionsTable;
